import React, { useState } from "react";
import { X, Plus, Minus, ShoppingBag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/format";
import { PRODUCTS, SUBSCRIBE_INTERVALS } from "@/data/catalog";
import { createShopifyCheckout } from "@/lib/shopify";

const SUGGESTED = [PRODUCTS.balm, PRODUCTS.patch, PRODUCTS.bundle];

const intervalLabel = (id) => SUBSCRIBE_INTERVALS.find((i) => i.id === id)?.label || "";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal, savings, count } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const url = await createShopifyCheckout(items);
      // Redirect to Shopify's secure hosted checkout.
      window.location.href = url;
    } catch (e) {
      toast.error("Checkout unavailable", {
        description: e.message || "Please try again in a moment.",
      });
      setLoading(false);
    }
  };

  const suggestions = SUGGESTED.filter((p) => !items.some((i) => i.id === p.id)).slice(0, 2);

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-[#6F503A]/25 backdrop-blur-sm z-40 transition-opacity"
          onClick={() => setOpen(false)}
          data-testid="cart-overlay"
        />
      )}
      <aside
        data-testid="cart-drawer"
        className={`fixed inset-y-0 right-0 w-full max-w-md bg-[#F8F5EF] shadow-2xl z-50 flex flex-col transform transition-transform duration-300 ease-in-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#6F503A]/10">
          <h2 className="font-heading text-lg font-semibold text-[#6F503A]">
            Your Cart {count > 0 && <span className="text-[#6F503A]/50">({count})</span>}
          </h2>
          <button onClick={() => setOpen(false)} data-testid="close-cart-btn" className="p-2 rounded-full hover:bg-[#E9E2D6]">
            <X className="w-5 h-5 text-[#6F503A]" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-8 text-center" data-testid="cart-empty">
            <div className="w-16 h-16 rounded-full bg-[#E9E2D6] flex items-center justify-center">
              <ShoppingBag className="w-7 h-7 text-[#6F503A]" />
            </div>
            <p className="text-[#6F503A]/70">Your cart is empty.</p>
            <button onClick={() => setOpen(false)} className="px-6 py-3 rounded-full bg-[#6F503A] text-[#F8F5EF] font-medium">
              Continue shopping
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {items.map((i) => (
                <div key={i.key} data-testid={`cart-item-${i.id}`} className="flex gap-4">
                  <div className="w-20 h-20 rounded-2xl bg-white overflow-hidden flex-shrink-0">
                    <img src={i.image} alt={i.name} className="w-full h-full object-contain p-2" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <h3 className="text-sm font-semibold text-[#6F503A] leading-snug">{i.name}</h3>
                      <button onClick={() => removeItem(i.key)} data-testid={`remove-${i.id}`} className="text-[#6F503A]/40 hover:text-[#6F503A]">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {i.sellingPlan === "subscribe" && (
                      <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-[#B8C4A3]/25 text-[#6F503A]">
                        Subscribe · {intervalLabel(i.interval)}
                      </span>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-[#6F503A]/15 rounded-full">
                        <button onClick={() => updateQty(i.key, i.qty - 1)} data-testid={`qty-dec-${i.id}`} className="p-1.5 text-[#6F503A]">
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="w-7 text-center text-sm text-[#6F503A]" data-testid={`qty-${i.id}`}>{i.qty}</span>
                        <button onClick={() => updateQty(i.key, i.qty + 1)} data-testid={`qty-inc-${i.id}`} className="p-1.5 text-[#6F503A]">
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-semibold text-[#6F503A]">{formatPrice(i.price * i.qty)}</span>
                        {i.compareAt && (
                          <span className="block text-xs text-[#6F503A]/40 line-through">{formatPrice(i.compareAt * i.qty)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {suggestions.length > 0 && (
                <div className="pt-4 border-t border-[#6F503A]/10">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#B8C4A3] mb-3">You may also like</p>
                  <div className="space-y-3">
                    {suggestions.map((p) => (
                      <Suggestion key={p.id} product={p} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-[#6F503A]/10 px-6 py-5 space-y-3 bg-[#F8F5EF]">
              {savings > 0 && (
                <div className="flex items-center justify-between text-sm" data-testid="cart-savings">
                  <span className="inline-flex items-center gap-1.5 text-[#6F503A] font-medium">
                    <span className="w-2 h-2 rounded-full bg-[#B8C4A3]" /> Bundle savings
                  </span>
                  <span className="text-[#6F503A] font-semibold">−{formatPrice(savings)}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-[#6F503A]/70">Subtotal</span>
                <span data-testid="cart-subtotal" className="font-heading text-xl font-semibold text-[#6F503A]">{formatPrice(subtotal)}</span>
              </div>
              <p className="text-xs text-[#6F503A]/50">Shipping & taxes calculated at checkout.</p>
              <button
                onClick={checkout}
                disabled={loading}
                data-testid="checkout-btn"
                className="w-full py-4 rounded-full bg-[#6F503A] text-[#F8F5EF] font-medium hover:bg-[#5b4130] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {loading ? "Processing…" : "Checkout"}
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
};

const Suggestion = ({ product }) => {
  const { addItem } = useCart();
  return (
    <div className="flex items-center gap-3" data-testid={`suggested-${product.handle}`}>
      <div className="w-12 h-12 rounded-xl bg-white overflow-hidden flex-shrink-0">
        <img src={product.image} alt={product.name} className="w-full h-full object-contain p-1.5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#6F503A] truncate">{product.name}</p>
        <p className="text-xs text-[#6F503A]/50">{formatPrice(product.price)}</p>
      </div>
      <button
        onClick={() => addItem(product, { qty: 1, sellingPlan: "onetime" })}
        data-testid={`suggested-add-${product.handle}`}
        className="px-3 py-1.5 rounded-full border border-[#6F503A]/20 text-[#6F503A] text-xs font-medium hover:bg-[#E9E2D6]"
      >
        Add
      </button>
    </div>
  );
};
